import React, { useState, useEffect } from 'react';
import { walletApi } from '../services/walletApi';
import { formatAddress } from '../utils/formatNames';
import WalletGroupBadge from './WalletGroupBadge';

const WalletDetail = ({ wallet, onUpdate, tokenId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [walletGroups, setWalletGroups] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [showGroupManager, setShowGroupManager] = useState(false);
  const [groupOperationLoading, setGroupOperationLoading] = useState(false);
  
  // Initial load
  useEffect(() => {
    if (wallet) {
      setEditName(wallet.name || '');
      setIsDefault(wallet.is_default || false);
      
      // Load wallet groups and all available groups
      loadWalletData();
    }
  }, [wallet, tokenId]);
  
  // Load wallet groups and all available groups
  const loadWalletData = async () => {
    if (!wallet) return;
    
    try {
      setLoading(true);
      
      // Load all groups to get full details with colors etc. - filter by token
      const [allGroupsData, walletsResponse] = await Promise.all([
        walletApi.getWalletGroups(tokenId),
        walletApi.getUserWallets(null, tokenId) // This gives us wallets with group info
      ]);
      
      setAllGroups(allGroupsData || []);
      
      // Find groups this wallet belongs to
      // We need to check which groups contain this wallet
      const walletGroupIds = [];
      
      // Check each group to see if this wallet is in it
      for (const group of allGroupsData || []) {
        try {
          const groupWallets = await walletApi.getUserWallets(group.id, tokenId);
          if (groupWallets.wallets?.some(w => w.id === wallet.id)) {
            walletGroupIds.push(group.id);
          }
        } catch (error) {
          console.error(`Error checking group ${group.id}:`, error);
        }
      }
      
      // Get the full group objects for groups this wallet belongs to
      const walletGroupsWithDetails = allGroupsData.filter(group => 
        walletGroupIds.includes(group.id)
      );
      
      setWalletGroups(walletGroupsWithDetails);
      setLoading(false);
    } catch (error) {
      console.error('Error loading wallet data:', error);
      setError('Failed to load wallet details.');
      setLoading(false);
    }
  };
  
  
  // Handle update wallet
  const handleUpdateWallet = async () => {
    if (!wallet) return;
    
    try {
      setLoading(true);
      
      // Update wallet
      const updatedWallet = await walletApi.updateWallet(wallet.id, {
        name: editName,
        is_default: isDefault
      });
      
      // Exit edit mode
      setIsEditing(false);
      
      // Notify parent to refresh and pass the updated wallet data
      if (onUpdate) {
        onUpdate(updatedWallet);
      }
    } catch (error) {
      console.error('Error updating wallet:', error);
      setError('Failed to update wallet.');
    } finally {
      setLoading(false);
    }
  };
  
  // Add wallet to group
  const handleAddToGroup = async (groupId) => {
    if (!wallet || !groupId) return;
    
    try {
      setGroupOperationLoading(true);
      await walletApi.addWalletsToGroup(groupId, [wallet.id]);
      
      // Refresh wallet data
      await loadWalletData();
      
      // Notify parent to refresh
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error adding wallet to group:', error);
      setError('Failed to add wallet to group.');
    } finally {
      setGroupOperationLoading(false);
    }
  };
  
  // Remove wallet from group
  const handleRemoveFromGroup = async (groupId) => {
    if (!wallet || !groupId) return;
    
    try {
      setGroupOperationLoading(true);
      await walletApi.removeWalletsFromGroup(groupId, [wallet.id]);
      
      // Refresh wallet data
      await loadWalletData();
      
      // Notify parent to refresh
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error removing wallet from group:', error);
      setError('Failed to remove wallet from group.');
    } finally {
      setGroupOperationLoading(false);
    }
  };
  
  // Handle copy to clipboard
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        if (type === 'address') {
          setCopiedAddress(true);
          setTimeout(() => setCopiedAddress(false), 2000);
        }
      })
      .catch(err => {
        console.error('Failed to copy:', err);
      });
  };
  
  // Get groups this wallet is NOT in
  const getAvailableGroups = () => {
    const walletGroupIds = walletGroups.map(g => g.id);
    return allGroups.filter(group => !walletGroupIds.includes(group.id));
  };
  
  if (!wallet) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 text-center text-gray-500 dark:text-gray-400 transition-colors">
        No wallet selected
      </div>
    );
  }
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-3 sm:p-4 md:p-6 transition-colors">
      {/* Header */}
      <div className="flex flex-col xs:flex-row xs:justify-between xs:items-start mb-4 sm:mb-6 gap-3 xs:gap-0">
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="text-lg sm:text-xl font-bold border-b-2 border-purple-300 dark:border-purple-600 focus:border-purple-600 dark:focus:border-purple-400 focus:outline-none pb-1 w-full bg-transparent text-gray-900 dark:text-gray-100"
              placeholder="Wallet Name"
            />
          ) : (
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-gray-100 break-words">
                {wallet.name || 'Unnamed Wallet'}
                {wallet.is_default && (
                  <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 px-2 py-0.5 rounded-full ml-2 inline-block mt-1 xs:mt-0">Default</span>
                )}
              </h2>
            </div>
          )}
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">ID: {wallet.id}</p>
        </div>

        <div className="flex flex-col xs:flex-row gap-2 xs:gap-2 w-full xs:w-auto">
          {isEditing ? (
            <>
              <button
                onClick={() => setIsEditing(false)}
                className="px-3 py-2 text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors order-2 xs:order-1"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateWallet}
                disabled={loading}
                className="px-3 py-2 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors disabled:bg-purple-400 order-1 xs:order-2"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="px-3 py-2 text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
            >
              Edit
            </button>
          )}
        </div>
      </div>
      
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 p-3 rounded mb-4 text-sm transition-colors">
          {error}
          <button 
            onClick={() => setError(null)} 
            className="ml-2 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-200"
          >
            ×
          </button>
        </div>
      )}
      
      {isEditing && (
        <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 transition-colors">
          <div className="mb-3">
            <label className="flex items-center text-sm text-gray-900 dark:text-gray-100">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="mr-2 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 transition-colors"
              />
              Set as default wallet
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-5">
              The default wallet will be automatically selected for new operations.
            </p>
          </div>
        </div>
      )}
      
      <div className="space-y-3 sm:space-y-4">
        {/* Wallet Groups Section - Now with better management */}
        <div className="p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 transition-colors">
          <div className="flex flex-col xs:flex-row xs:justify-between xs:items-center mb-3 gap-2 xs:gap-0">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Groups</h3>
            <button
              onClick={() => setShowGroupManager(!showGroupManager)}
              className="text-xs sm:text-sm text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200 flex items-center self-start xs:self-auto"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              <span className="hidden xs:inline">Manage Groups</span>
              <span className="xs:hidden">Manage</span>
            </button>
          </div>
          
          {loading ? (
            <div className="flex justify-center p-4">
              <div className="w-5 h-5 border-2 border-gray-300 border-t-purple-600 rounded-full animate-spin"></div>
            </div>
          ) : walletGroups.length > 0 ? (
            <div className="space-y-2">
              {walletGroups.map(group => (
                <div key={group.id} className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <WalletGroupBadge group={group} showName={true} />
                  </div>
                  <button
                    onClick={() => handleRemoveFromGroup(group.id)}
                    disabled={groupOperationLoading}
                    className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 disabled:opacity-50 flex-shrink-0"
                    title="Remove from group"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This wallet is not part of any group.
            </p>
          )}
          
          {/* Group Management Section */}
          {showGroupManager && (
            <div className="mt-4 pt-4 border-t border-gray-300 dark:border-gray-600">
              <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Add to Group:</h4>
              <div className="space-y-2">
                {getAvailableGroups().length > 0 ? (
                  getAvailableGroups().map(group => (
                    <div key={group.id} className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <WalletGroupBadge group={group} showName={true} />
                      </div>
                      <button
                        onClick={() => handleAddToGroup(group.id)}
                        disabled={groupOperationLoading}
                        className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200 disabled:opacity-50 flex-shrink-0"
                      >
                        Add
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    This wallet is already in all available groups.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Wallet Address */}
        <div className="p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 transition-colors">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Public Address</h3>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="bg-white dark:bg-gray-800 p-2 rounded border border-gray-300 dark:border-gray-600 text-xs sm:text-sm font-mono flex-1 break-all text-gray-900 dark:text-gray-100 transition-colors">
              {wallet.address}
            </div>
            <button
              onClick={() => copyToClipboard(wallet.address, 'address')}
              className="self-start sm:self-stretch px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-purple-700 dark:hover:text-purple-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-300 dark:border-gray-600 flex items-center justify-center gap-2"
              title="Copy address"
            >
              {copiedAddress ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                  <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                </svg>
              )}
              <span className="text-xs sm:text-sm hidden xs:inline">{copiedAddress ? 'Copied!' : 'Copy'}</span>
            </button>
          </div>
        </div>

        {/* Created Date */}
        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 transition-colors">
          <span className="font-medium">Created:</span> {new Date(wallet.created_at).toLocaleString()}
        </div>
      </div>
    </div>
  );
};

export default WalletDetail;